import { Button, ExternalLink, Text } from '@ui/themed';
import { t } from 'i18next';
import { useChecklistItemCtx, useSkipStep } from '../../../../components/checklist';
import { UnorderedList } from '../../../../components/unordered-list/unordered-list';
import { StepWithPreview } from '../../shared/components/step-with-preview';
import type { OnboardingChecklistStep } from '../../shared/types';
import { CartGraphic } from './cart-graphic';
import { CartPrefillStatusLine } from './cart-prefill-status-line';
import { useCartPrefill } from './use-cart-prefill';
import { useChecklistStatus } from './use-checklist-status';
import { useInitiateTestOrder } from './use-initiate-test-order';

export const ORDER_STEP: OnboardingChecklistStep = {
  icon: 'store',
  // id stays 'place-order' so a user's skipped-step state (persisted as `<scope>:place-order:skipped`)
  // survives the rename of this step to "order".
  id: 'place-order',
  title: t('features:onboarding.components.order_item.title'),
  useStatus: useChecklistStatus,
  children: <PlaceOrderBody />,
};

const PLACE_ORDER_DOCS_URL = 'https://docs.sparklayer.io/orders';

// Preview lives on the shared wrapper so it stays put across states; only the left column swaps.
function PlaceOrderBody() {
  const { status } = useChecklistItemCtx();

  return (
    <StepWithPreview preview={<CartGraphic />}>
      {status === 'complete' ? <PlaceOrderComplete /> : <PlaceOrderInstructions />}
    </StepWithPreview>
  );
}

function PlaceOrderComplete() {
  return <Text variant="secondary">{t('features:onboarding.components.order_item.complete')}</Text>;
}

// In-progress content. The cart prefill is owned here rather than the body so it only ever runs
// while the step is actually incomplete.
function PlaceOrderInstructions() {
  const prefillStatus = useCartPrefill();
  const { initiateTestOrder, buyerEmail, isReady, hasInitiated } = useInitiateTestOrder();
  const { isSkipped, skip } = useSkipStep();

  return (
    <>
      <Text>
        {t('features:onboarding.components.order_item.description')}{' '}
        <ExternalLink link={PLACE_ORDER_DOCS_URL} showIcon>
          {t('features:onboarding.components.order_item.learn_more')}
        </ExternalLink>
      </Text>

      <UnorderedList>
        <UnorderedList.Item>
          {t('features:onboarding.components.order_item.instruction_sign_in', {
            email: buyerEmail,
          })}
        </UnorderedList.Item>
        <UnorderedList.Item>
          {prefillStatus === 'manual'
            ? t('features:onboarding.components.order_item.instruction_cart_manual')
            : t('features:onboarding.components.order_item.instruction_cart')}
        </UnorderedList.Item>
        <UnorderedList.Item>
          {t('features:onboarding.components.order_item.instruction_checkout')}
        </UnorderedList.Item>
      </UnorderedList>

      <CartPrefillStatusLine status={prefillStatus} />

      <div className="flex flex-wrap items-center gap-2">
        <Button
          variant="primary"
          icon="externalLink"
          disabled={!isReady}
          onClick={initiateTestOrder}
        >
          {t('features:onboarding.components.order_item.cta')}
        </Button>
        {!isSkipped && (
          <Button variant="bare" onClick={skip}>
            {t('features:onboarding.components.order_item.skip')}
          </Button>
        )}
      </div>

      {hasInitiated && (
        <Text variant="secondary">{t('features:onboarding.components.order_item.waiting')}</Text>
      )}
    </>
  );
}
